import { useLangStore, type Lang } from '@/stores/langStore';
import { COMMON_LABELS } from './labels.common';
import { TENANTS_LABELS } from './labels.tenants';
import { AGENTS_LABELS } from './labels.agents';
import { KNOWLEDGE_LABELS } from './labels.knowledge';
import { OPS_LABELS } from './labels.ops';
import { EMAIL_LABELS } from './labels.email';
import { SETTINGS_LABELS } from './labels.settings';
import { MY_SETTINGS_LABELS } from './labels.mySettings';
import { UIUX_LABELS } from './labels.uiux';
// labels.bots.ts is GONE (OP-559) — it translated two unrouted pages that called a route
// (/api/bots) the gateway never had. The surviving bot vocabulary is `botEntity*` (Bot
// Attachments, in labels.knowledge.ts) plus the `bots*` page keys in CORE_LABELS below.
import { ANALYTICS_LABELS } from './labels.analytics';
import { BOT_MIND_LABELS } from './labels.botMind';
import { TIMELINE_LABELS } from './labels.timeline';
import { TAGS_LABELS } from './labels.tags';
import { SITE_MAP_LABELS } from './labels.siteMap';
import { KNOWLEDGE_LIBRARY_LABELS } from './labels.knowledgeLibrary';
import { BOT_SKILLS_LABELS } from './labels.botSkills';
import { LEARNING_ARTICLES_LABELS } from './labels.learningArticles';

/**
 * Bilingual (EN/HE) label catalog + enum mappers for the unified staff portal
 * (ADR-012). This is the ONE place UI strings live; components call `useT()`
 * for chrome strings and the enum mappers for backend values.
 *
 * Ported from agent-console's i18n (Phase-6 i18n unification) and extended to
 * cover the host admin-portal chrome + screens (admin-portal previously had no
 * i18n — 100% hardcoded English).
 *
 * Rules:
 *  - DISPLAY ONLY. Never feed a label back to an API or use it as a key —
 *    wire values (enum strings, learning-tag values) are unchanged.
 *  - Tests key off `data-testid`, not visible text, so adding/changing a label
 *    here is safe for the suite.
 */

interface Entry {
  en: string;
  he: string;
}

/**
 * Core catalog — app shell, common chrome, and the workspace module. Screen
 * catalogs are split into `labels.<cluster>.ts` modules (spread into LABELS
 * below) so parallel localization work doesn't collide on one file.
 */
const CORE_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Host shell — app name, header, nav (admin-portal chrome)
  // ═══════════════════════════════════════════════════════════════════════
  appName: { en: 'Staff Portal', he: 'פורטל צוות' },
  languageToggle: { en: 'עברית', he: 'English' },
  headerLogout: { en: 'Logout', he: 'התנתק' },
  headerSelectTenant: { en: 'Select tenant', he: 'בחר ארגון' },
  headerAllTenants: { en: 'All tenants', he: 'כל הארגונים' },
  headerDevChipTitle: {
    en: 'Developer access — audit logs & diagnostics',
    he: 'גישת מפתח — יומני ביקורת ואבחון',
  },
  roleLabelPlatformAdmin: { en: 'Platform Admin', he: 'מנהל פלטפורמה' },
  roleLabelAdmin: { en: 'Admin', he: 'מנהל' },
  roleLabelSeniorAgent: { en: 'Senior Agent', he: 'נציג בכיר' },
  roleLabelAgent: { en: 'Agent', he: 'נציג' },

  // Sidebar nav items (mirror Sidebar.tsx navItems)
  navAriaMain: { en: 'Main navigation', he: 'ניווט ראשי' },
  navDashboard: { en: 'Dashboard', he: 'לוח בקרה' },
  // navWorkspace is no longer shown in the sidebar (the workspace is retired, ADR-033 D12); the key is kept
  // because the i18n lookup test pins it. The pages that replace it are the agentConsole* keys below.
  navWorkspace: { en: 'Live Conversations', he: 'שיחות פעילות' },

  // Retired /workspace -> Agent Console redirect (src/pages/AgentConsoleRedirect.tsx, ADR-033 D12).
  agentConsoleMovedTitle: {
    en: 'The staff workspace moved to the Agent Console',
    he: 'סביבת העבודה של הצוות עברה לקונסולת הנציגים',
  },
  agentConsoleRedirecting: {
    en: 'Taking you to the Agent Console…',
    he: 'מעביר אותך לקונסולת הנציגים…',
  },
  agentConsoleOpenLink: { en: 'Open the Agent Console', he: 'פתח את קונסולת הנציגים' },
  agentConsoleAskAdmin: {
    en: 'Ask your administrator for the address of the Agent Console.',
    he: 'בקש ממנהל המערכת את הכתובת של קונסולת הנציגים.',
  },
  agentConsoleAddressInvalid: {
    en: 'The Agent Console address configured for this portal is not valid. Ask your administrator for the correct address.',
    he: 'כתובת קונסולת הנציגים שהוגדרה בפורטל זה אינה תקינה. בקש ממנהל המערכת את הכתובת הנכונה.',
  },
  navTenants: { en: 'Tenants', he: 'ארגונים' },
  navUsers: { en: 'Users', he: 'משתמשים' },
  navGlobalMind: { en: 'Global Mind', he: 'מוח גלובלי' },
  navSettings: { en: 'Settings', he: 'הגדרות' },
  navConversationStates: { en: 'Conversation Statuses', he: 'סטטוסי שיחה' },
  navAgents: { en: 'Agents', he: 'סוכנים' },
  navKnowledgeBanks: { en: 'Knowledge Banks', he: 'מאגרי ידע' },
  navLearning: { en: 'Learning', he: 'למידה' },
  navTemplates: { en: 'Templates', he: 'תבניות' },
  navChannels: { en: 'Channels', he: 'ערוצים' },
  navBots: { en: 'Bots', he: 'בוטים' },
  navConfiguration: { en: 'Configuration', he: 'תצורה' },
  navAnalytics: { en: 'Analytics', he: 'אנליטיקה' },
  navAuditLog: { en: 'Audit Log', he: 'יומן ביקורת' },
  navTicketImportMapping: { en: 'Ticket Import Mapping', he: 'מיפוי ייבוא כרטיסים' },

  // Bot Management — collapsible nav group (see Sidebar.tsx navEntries).
  // `navBotsKnowledge` labels the MEMORY CONSOLE at /bot-mind — the four memory
  // tiers are what the bot knows. Knowledge Banks keeps `navKnowledgeBanks` and
  // stays a top-level entry.
  navBotManagement: { en: 'Bot Management', he: 'ניהול בוטים' },
  navBotsKnowledge: { en: "Bot's Knowledge", he: 'ידע הבוט' },
  /** Accessible-name suffix on a collapsible nav-group header button. */
  navGroupToggleAria: { en: 'expand or collapse section', he: 'הרחב או כווץ מקטע' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Common chrome (shared components + generic actions)
  // ═══════════════════════════════════════════════════════════════════════
  commonLoading: { en: 'Loading…', he: 'טוען…' },
  commonSave: { en: 'Save', he: 'שמור' },
  commonSaving: { en: 'Saving…', he: 'שומר…' },
  commonCancel: { en: 'Cancel', he: 'בטל' },
  commonDelete: { en: 'Delete', he: 'מחק' },
  commonEdit: { en: 'Edit', he: 'ערוך' },
  commonCreate: { en: 'Create', he: 'צור' },
  commonBack: { en: 'Back', he: 'חזרה' },
  commonSearch: { en: 'Search', he: 'חיפוש' },
  commonConfirm: { en: 'Confirm', he: 'אשר' },
  commonDismiss: { en: 'Dismiss', he: 'סגור' },
  commonRetry: { en: 'Retry', he: 'נסה שוב' },
  commonNoResults: { en: 'No results', he: 'אין תוצאות' },
  forbiddenTitle: { en: 'Access denied', he: 'הגישה נדחתה' },
  forbiddenMessage: {
    en: "You don't have permission to view this page.",
    he: 'אין לך הרשאה לצפות בדף זה.',
  },
  errorBoundaryTitle: { en: 'Something went wrong', he: 'משהו השתבש' },
  errorBoundaryMessage: {
    en: 'An unexpected error occurred. Please reload the page.',
    he: 'אירעה שגיאה בלתי צפויה. אנא טען מחדש את הדף.',
  },
  saveErrorGeneric: { en: 'Could not save. Please try again.', he: 'השמירה נכשלה. נסה שוב.' },
  loadErrorGeneric: { en: 'Could not load. Please try again.', he: 'הטעינה נכשלה. נסה שוב.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Workspace module (ported live-chat — preserve behavior/UX exactly)
  // ═══════════════════════════════════════════════════════════════════════
  agentFallbackName: { en: 'Agent', he: 'נציג' },
  noActiveConversations: { en: 'No active conversations', he: 'אין שיחות פעילות' },
  noMessagesYet: { en: 'No messages yet', he: 'אין הודעות עדיין' },
  unreadSuffix: { en: 'new', he: 'חדש' },
  workspaceOnline: { en: 'Agents Online: {n}', he: 'נציגים מחוברים: {n}' },

  // ConversationPanel
  selectConversation: {
    en: 'Select a conversation to view messages',
    he: 'בחר שיחה כדי לצפות בהודעות',
  },
  consultationTypeHint: {
    en: 'Type guidance below — the bot will relay it to the customer.',
    he: 'הקלד הנחיה למטה — הבוט יעביר אותה ללקוח.',
  },
  consultationProactiveHint: {
    en: 'Type guidance below to advise the bot at any time.',
    he: 'הקלד הנחיה למטה כדי לייעץ לבוט בכל עת.',
  },
  surfaceCustomerChat: { en: 'Customer Chat', he: 'צ׳אט לקוח' },
  surfaceBotConsultation: { en: 'Bot Consultation', he: 'ייעוץ לבוט' },
  guidanceNotDelivered: { en: 'GUIDANCE NOT DELIVERED', he: 'ההנחיה לא הועברה' },
  openConsultationDot: { en: 'open consultation', he: 'ייעוץ פתוח' },

  // GuidancePanel (was hardcoded English in agent-console — localized here)
  guidancePlaceholder: {
    en: 'Type guidance or message…',
    he: 'הקלד הנחיה או הודעה…',
  },
  guidanceSend: { en: 'Send', he: 'שלח' },

  // LayoutModeToggle (was hardcoded English in agent-console — localized here)
  layoutTabs: { en: 'Tabs', he: 'כרטיסיות' },
  layoutSplit: { en: 'Split', he: 'מפוצל' },
  layoutModeAria: { en: 'Conversation layout mode', he: 'מצב פריסת שיחה' },

  // CloseConversationButton
  closeConversation: { en: 'Close conversation', he: 'סגור שיחה' },
  closeConfirm: { en: 'Confirm close', he: 'אשר סגירה' },
  closeClosing: { en: 'Closing…', he: 'סוגר…' },
  closeCancel: { en: 'Cancel', he: 'בטל' },
  closeFailed: { en: 'Close failed — try again', he: 'הסגירה נכשלה — נסה שוב' },

  // TakeoverButton
  takeover: { en: 'Takeover', he: 'קח אחריות' },
  takingOver: { en: 'Taking over…', he: 'לוקח אחריות…' },

  // ReleaseButton
  releaseToAi: { en: 'Release to AI', he: 'החזר לבוט' },
  releasing: { en: 'Releasing…', he: 'משחרר…' },

  // LetBotContinueButton
  letBotContinue: { en: 'Let Bot Continue', he: 'תן לבוט להמשיך' },
  letBotContinueTitle: {
    en: 'Release this consultation. The bot will reply with its fallback message and resume handling.',
    he: 'שחרר את הייעוץ. הבוט ישלח הודעת ברירת מחדל וימשיך לטפל.',
  },
  letBotReleasing: { en: 'Releasing…', he: 'משחרר…' },

  // AgentReplyInput
  replyToCustomer: { en: 'REPLY TO CUSTOMER', he: 'תגובה ללקוח' },
  replyPlaceholder: {
    en: 'Type your reply — Enter to send, Shift+Enter for newline',
    he: 'הקלד תגובה — Enter לשליחה, Shift+Enter לשורה חדשה',
  },
  replySend: { en: 'Send', he: 'שלח' },
  replySending: { en: 'Sending…', he: 'שולח…' },
  replyAttach: { en: 'Attach a file', he: 'צרף קובץ' },
  replyFailed: { en: 'Message failed to send — try again', he: 'שליחת ההודעה נכשלה — נסה שוב' },

  // LearningTagControl
  learningHeader: { en: 'Teach the bot from this chat', he: 'למד את הבוט מהשיחה' },
  learningOptionDefault: { en: 'Normal', he: 'רגיל' },
  learningOptionSkip: { en: "Don't learn", he: 'אל תלמד' },
  learningOptionForceLearn: { en: 'Always learn', he: 'למד תמיד' },
  learningOptionDefaultTitle: {
    en: 'Let the bot decide what is worth remembering from this chat.',
    he: 'תן לבוט להחליט מה שווה לזכור מהשיחה הזו.',
  },
  learningOptionSkipTitle: {
    en: 'Do not learn from this session.',
    he: 'אל תלמד מהשיחה הזו.',
  },
  learningOptionForceLearnTitle: {
    en: 'Always learn from this session, even if the signal is weak.',
    he: 'למד תמיד מהשיחה הזו, גם אם האות חלש.',
  },
  learningNotePlaceholder: {
    en: 'Optional note for training…',
    he: 'הערה לאימון (לא חובה)…',
  },
  learningNoteAriaLabel: { en: 'Training note', he: 'הערת אימון' },
  learningTagAriaLabel: { en: 'Learning setting', he: 'הגדרת למידה' },
  learningSave: { en: 'Save', he: 'שמור' },
  learningSaving: { en: 'Saving…', he: 'שומר…' },
  learningFailedToSave: {
    en: 'Failed to save. Please try again.',
    he: 'השמירה נכשלה. נסה שוב.',
  },

  // Availability toggle (header, workspace)
  availabilityAria: { en: 'Set availability', he: 'הגדר זמינות' },

  // Conversation-label fallbacks
  guestChatLabel: { en: 'Guest chat #{code}', he: 'שיחה עם אורח #{code}' },
  noSubject: { en: '(no subject)', he: '(ללא נושא)' },
  anonymous: { en: 'Anonymous', he: 'אנונימי' },
  anonymousWithMessage: { en: 'Anonymous - {msg}', he: 'אנונימי - {msg}' },

  // SLA countdown
  slaRemaining: { en: '{n}m left', he: 'נותרו {n} ד׳' },
  slaBreached: { en: 'Overdue by {n}m', he: 'באיחור של {n} ד׳' },

  // Action-failure + load states
  actionFailedRetry: { en: 'Action failed — try again', he: 'הפעולה נכשלה — נסה שוב' },
  conversationsLoading: { en: 'Loading conversations…', he: 'טוען שיחות…' },
  conversationsLoadError: {
    en: "Couldn't load conversations — they'll appear here as they arrive.",
    he: 'לא ניתן לטעון את השיחות — הן יופיעו כאן כשייכנסו.',
  },
  messagesLoadError: {
    en: "Couldn't load this conversation's history — try reopening it.",
    he: 'לא ניתן לטעון את היסטוריית השיחה — נסה לפתוח אותה מחדש.',
  },
  dismiss: { en: 'Dismiss', he: 'סגור' },

  // OP-327 — reconnect banner (workspace hub)
  hubReconnectingBanner: {
    en: 'Connection lost — reconnecting…',
    he: 'החיבור אבד — מתחבר מחדש…',
  },
  // ═══════════════════════════════════════════════════════════════════════
  //  ADR-016 — load-based routing (workspace badge + routed-card read-only)
  // ═══════════════════════════════════════════════════════════════════════
  loadBadgeTooltip: {
    en: 'Your open conversations / your max concurrent conversations',
    he: 'השיחות הפתוחות שלך / מקסימום השיחות המקבילות שלך',
  },
  assignedToAnotherAgent: { en: 'Assigned to another agent', he: 'הוקצה לנציג אחר' },
  assignedToAnotherAgentTitle: {
    en: 'This consultation was routed to another agent. You can view it for awareness, but only the assigned agent can act on it.',
    he: 'ייעוץ זה נותב לנציג אחר. ניתן לצפות בו לצורך מודעות, אך רק הנציג שהוקצה יכול לפעול עליו.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  ADR-017 D3 — conversation directory (collapsible per-category sections)
  // ═══════════════════════════════════════════════════════════════════════
  uncategorizedSection: { en: 'No status', he: 'ללא סטטוס' },
  toggleSectionAria: { en: 'Toggle conversation status section', he: 'הרחב או כווץ סטטוס שיחה' },

  // ═══════════════════════════════════════════════════════════════════════
  //  WORKSTREAM D — CRM previous conversations (HubSpot interactions)
  // ═══════════════════════════════════════════════════════════════════════
  crmHistoryTitle: { en: 'Previous conversations', he: 'שיחות קודמות' },
  crmHistoryLoading: { en: 'Loading history…', he: 'טוען היסטוריה…' },
  crmHistoryEmpty: { en: 'No past interactions', he: 'אין אינטראקציות קודמות' },
  crmHistoryUnavailable: { en: 'CRM history unavailable', he: 'היסטוריית CRM אינה זמינה' },

  // ═══════════════════════════════════════════════════════════════════════
  //  ADR-016 — Human Agent Settings (per-agent 0–10 cap on the Users page)
  // ═══════════════════════════════════════════════════════════════════════
  agentCapSectionTitle: { en: 'Human Agent Settings', he: 'הגדרות נציג אנושי' },
  agentCapLabel: { en: 'Max concurrent conversations', he: 'מקסימום שיחות מקבילות' },
  agentCapHelper: {
    en: '0–10. 0 = do not route new conversations to this agent.',
    he: '0–10. 0 = אל תנתב שיחות חדשות לנציג זה.',
  },
  agentCapRangeError: {
    en: 'Enter a whole number from 0 to 10.',
    he: 'הזן מספר שלם בין 0 ל‑10.',
  },
  agentCapLoadError: {
    en: "Couldn't load this agent's cap. Try reopening the page.",
    he: 'לא ניתן לטעון את מכסת הנציג. נסה לפתוח מחדש את הדף.',
  },
  agentCapSaved: { en: 'Saved.', he: 'נשמר.' },
  agentCapSaveError: {
    en: 'Could not save the cap. Please try again.',
    he: 'לא ניתן לשמור את המכסה. נסה שוב.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Bots page (/bots) — in-page tabs over Channels | Agents | Bot Attachments
  //  Tab labels reuse `navChannels` / `navAgents`; only the renamed
  //  /bot-entities surface needs a new key.
  // ═══════════════════════════════════════════════════════════════════════
  botsPageTitle: { en: 'Bots', he: 'בוטים' },
  botsPageDescription: {
    en: 'Channels, agent configurations, and the bot attachments that bind them.',
    he: 'ערוצים, תצורות סוכן, והשיוכים שמקשרים ביניהם.',
  },
  botsSectionsAria: { en: 'Bot sections', he: 'מקטעי בוטים' },
  botsTabAttachments: { en: 'Bot Attachments', he: 'שיוכי בוט' },
} satisfies Record<string, Entry>;

/**
 * The full label catalog: core shell/chrome/workspace + every screen cluster.
 * Spreads keep each cluster editable in its own module (parallel-safe) while a
 * single `LABELS` preserves literal-key type-safety for {@link useT}/{@link translate}.
 */
export const LABELS = {
  ...CORE_LABELS,
  ...COMMON_LABELS,
  ...TENANTS_LABELS,
  ...AGENTS_LABELS,
  ...KNOWLEDGE_LABELS,
  ...OPS_LABELS,
  ...EMAIL_LABELS,
  ...SETTINGS_LABELS,
  ...MY_SETTINGS_LABELS,
  ...UIUX_LABELS,
  ...ANALYTICS_LABELS,
  ...BOT_MIND_LABELS,
  ...TIMELINE_LABELS,
  ...TAGS_LABELS,
  ...SITE_MAP_LABELS,
  ...KNOWLEDGE_LIBRARY_LABELS,
  ...BOT_SKILLS_LABELS,
  ...LEARNING_ARTICLES_LABELS,
} satisfies Record<string, Entry>;

export type LabelKey = keyof typeof LABELS;

export function translate(key: LabelKey, lang: Lang): string {
  return LABELS[key][lang];
}

// ─── Enum mappers (backend value → friendly bilingual label) ─────────
// Each takes the raw backend string and falls back to the raw value if it's
// an unknown variant, so a new server enum never renders blank.

// Also doubles as the friendly-label map for the ADR-017 `ConversationStateDef.mapsToLifecycle`
// enum (NEW/ACTIVE/CONSULTING/HUMAN_CONTROLLED/CLOSED) shown on the tenant-admin
// Conversation States settings page — a related but distinctly-named lifecycle enum from
// the live workspace conversation-list state above. Keys don't collide; `pick()`'s
// raw-value fallback means either enum renders safely even if the other adds a variant.
const STATE: Record<string, Entry> = {
  AI_HANDLING: { en: 'Bot', he: 'בוט' },
  CONSULTATION_REQUESTED: { en: 'Needs help', he: 'דרושה עזרה' },
  HUMAN_TAKEOVER: { en: 'With agent', he: 'אצל נציג' },
  CLOSED: { en: 'Closed', he: 'סגור' },
  NEW: { en: 'New', he: 'חדש' },
  ACTIVE: { en: 'Active (bot handling)', he: 'פעיל (הבוט מטפל)' },
  CONSULTING: { en: 'Bot asked for help', he: 'הבוט ביקש עזרה' },
  HUMAN_CONTROLLED: { en: 'With a human agent', he: 'אצל נציג אנושי' },
};

const URGENCY: Record<string, Entry> = {
  CRITICAL: { en: 'Urgent', he: 'דחוף' },
  WARNING: { en: 'Due soon', he: 'בקרוב' },
  NORMAL: { en: 'On track', he: 'תקין' },
};

const SENDER: Record<string, Entry> = {
  CUSTOMER: { en: 'Customer', he: 'לקוח' },
  AI: { en: 'Bot', he: 'בוט' },
  AGENT: { en: 'Agent', he: 'נציג' },
};

const AVAILABILITY: Record<string, Entry> = {
  ONLINE: { en: 'Available', he: 'זמין' },
  BUSY: { en: 'Busy', he: 'עסוק' },
  OFFLINE: { en: 'Offline', he: 'לא מחובר' },
};

const HUB_STATUS: Record<string, Entry> = {
  connected: { en: 'Connected', he: 'מחובר' },
  connecting: { en: 'Connecting…', he: 'מתחבר…' },
  reconnecting: { en: 'Reconnecting…', he: 'מתחבר מחדש…' },
  disconnected: { en: 'Offline', he: 'לא מחובר' },
};

const CHANNEL: Record<string, Entry> = {
  webchat: { en: 'Web chat', he: 'צ׳אט באתר' },
  whatsapp: { en: 'WhatsApp', he: 'וואטסאפ' },
  sms: { en: 'SMS', he: 'SMS' },
  email: { en: 'Email', he: 'אימייל' },
  messenger: { en: 'Messenger', he: 'מסנג׳ר' },
};

// Audit-log severity (backend SeverityCode enum). Plain-language names for a
// non-technical admin — "Debug"/"Milestone"/"Fatal" read as raw engineering
// terms otherwise (AuditLogPage — both filter dropdown and table cell).
const SEVERITY: Record<string, Entry> = {
  Debug: { en: 'Diagnostic', he: 'אבחון' },
  Info: { en: 'Info', he: 'מידע' },
  Milestone: { en: 'Key event', he: 'אירוע מרכזי' },
  Warning: { en: 'Warning', he: 'אזהרה' },
  Fatal: { en: 'Critical error', he: 'שגיאה קריטית' },
};

// Audit-log serviceName (backend kebab-case service identifier, e.g.
// "ai-agent", "channel-adapter"). Never show the raw identifier to an admin —
// it exposes backend architecture and means nothing to them (AuditLogPage).
const SERVICE_NAME: Record<string, Entry> = {
  'api-gateway': { en: 'Platform Gateway', he: 'שער הפלטפורמה' },
  'channel-adapter': { en: 'Messaging Channels', he: 'ערוצי הודעות' },
  'realtime-chat': { en: 'Live Chat', he: 'צ׳אט חי' },
  'identity-auth': { en: 'Sign-in & Accounts', he: 'התחברות וחשבונות' },
  'session-management': { en: 'Chat Sessions', he: 'הפעלות צ׳אט' },
  'conversation-orchestrator': { en: 'Conversation Flow', he: 'ניהול זרימת שיחה' },
  'ai-agent': { en: 'AI Assistant', he: 'עוזר ה-AI' },
  'knowledge-base': { en: 'Knowledge Base', he: 'מאגר ידע' },
  'crm-integration': { en: 'CRM Integration', he: 'שילוב CRM' },
  'escalation-queue': { en: 'Escalations', he: 'הסלמות' },
  'agent-hub': { en: 'Agent Handoff', he: 'העברה לנציג' },
  'conversation-store': { en: 'Conversation History', he: 'היסטוריית שיחות' },
  'media-attachment': { en: 'File Attachments', he: 'קבצים מצורפים' },
  notification: { en: 'Notifications', he: 'התראות' },
  'template-management': { en: 'Message Templates', he: 'תבניות הודעה' },
  'analytics-reporting': { en: 'Reports & Analytics', he: 'דוחות וניתוחים' },
  'audit-compliance': { en: 'Audit & Compliance', he: 'ביקורת ותאימות' },
  'tenant-management': { en: 'Account Management', he: 'ניהול חשבונות' },
  'configuration-management': { en: 'Settings', he: 'הגדרות' },
  learning: { en: 'Learning', he: 'למידה' },
  'agent-memory': { en: 'Bot Memory', he: 'זיכרון הבוט' },
  system: { en: 'Platform', he: 'פלטפורמה' },
};

function pick(map: Record<string, Entry>, raw: string, lang: Lang): string {
  return map[raw]?.[lang] ?? raw;
}

export const stateLabel = (raw: string, lang: Lang) => pick(STATE, raw, lang);
export const urgencyLabel = (raw: string, lang: Lang) => pick(URGENCY, raw, lang);
export const senderLabel = (raw: string, lang: Lang) => pick(SENDER, raw, lang);
export const availabilityLabel = (raw: string, lang: Lang) => pick(AVAILABILITY, raw, lang);
export const hubStatusLabel = (raw: string, lang: Lang) => pick(HUB_STATUS, raw, lang);
export const channelLabel = (raw: string, lang: Lang) =>
  pick(CHANNEL, (raw ?? '').toLowerCase(), lang);
export const severityLabel = (raw: string, lang: Lang) => pick(SEVERITY, raw, lang);
export const serviceLabel = (raw: string, lang: Lang) =>
  pick(SERVICE_NAME, (raw ?? '').toLowerCase(), lang);

// ─── React hook ──────────────────────────────────────────────────────

/**
 * Subscribe a component to the current language and get bound translators.
 * Re-renders when the user flips the language toggle.
 */
export function useT() {
  const lang = useLangStore((s) => s.lang);
  return {
    lang,
    t: (key: LabelKey) => translate(key, lang),
    stateLabel: (raw: string) => stateLabel(raw, lang),
    urgencyLabel: (raw: string) => urgencyLabel(raw, lang),
    senderLabel: (raw: string) => senderLabel(raw, lang),
    availabilityLabel: (raw: string) => availabilityLabel(raw, lang),
    hubStatusLabel: (raw: string) => hubStatusLabel(raw, lang),
    channelLabel: (raw: string) => channelLabel(raw, lang),
    severityLabel: (raw: string) => severityLabel(raw, lang),
    serviceLabel: (raw: string) => serviceLabel(raw, lang),
  };
}
